import { Box, Grid, Stack, Typography } from "@mui/material";
import type { SourceRow } from "@/types";
import { countryName } from "@/utils/countryName.ts";
import { formatAsn } from "@/utils/formatAsn.ts";
import { formatDateTime } from "@/utils/formatDateTime.ts";
import * as copy from "@/utils/vulnerabilityCopy.ts";

function DetailLine({ label, value, caption }: { readonly label: string; readonly value: string; readonly caption?: string }) {
  return (
    <Box>
      <Typography variant="body2">
        <Box component="span" sx={{ color: "text.secondary", mr: 1 }}>{label}</Box>
        {value}
      </Typography>
      {caption && <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>{caption}</Typography>}
    </Box>
  );
}

function epochOrNotKnown(epoch: number | null): string {
  return epoch === null ? copy.NOT_KNOWN : formatDateTime(epoch);
}

function Facts({ row }: { readonly row: SourceRow }) {
  return (
    <Stack spacing={0.5}>
      <DetailLine label={copy.FILTER_COUNTRY} value={row.country_code ? countryName(row.country_code) : copy.NOT_KNOWN} />
      <DetailLine label={copy.FILTER_NETWORK} value={formatAsn(row.asn, row.as_name) ?? copy.NOT_KNOWN} />
      <DetailLine label={copy.FILTER_FIRST_SEEN} value={epochOrNotKnown(row.first_seen)} />
      <DetailLine label={copy.COL_LAST_SEEN} value={epochOrNotKnown(row.last_seen)} />
      <DetailLine label={copy.FILTER_WHO} value={copy.actorClassLabel(row.actor_class)} />
      <DetailLine label={copy.NAMED_LATER} value={row.named_later.toLocaleString("en-US")} />
      <DetailLine label={copy.KPI_YOUR_PRODUCT} value={copy.yourProductLabel(row.your_product)} />
    </Stack>
  );
}

function SignIn({ row }: { readonly row: SourceRow }) {
  const { attempts } = row;
  return (
    <Stack spacing={0.5}>
      <DetailLine label={copy.FILTER_STEP} value={copy.stepLabel(row.furthest_step)} />
      <DetailLine label={copy.FILTER_ANSWER} value={copy.answerLabel(row.answer)} />
      <DetailLine label={copy.ATTEMPT_RESULTS.blocked} value={attempts.blocked.toLocaleString("en-US")} />
      <DetailLine label={copy.ATTEMPT_RESULTS.userNotFound} value={attempts.user_not_found.toLocaleString("en-US")} />
      <DetailLine label={copy.ATTEMPT_RESULTS.invalidPassword} value={attempts.invalid_password.toLocaleString("en-US")} />
      <DetailLine label={copy.ATTEMPT_RESULTS.failedSecondFactor} value={attempts.failed_second_factor.toLocaleString("en-US")} />
      <DetailLine label={copy.ATTEMPT_RESULTS.signedIn} value={attempts.signed_in.toLocaleString("en-US")} />
    </Stack>
  );
}

export default function SourceDetail({ row }: { readonly row: SourceRow }) {
  return (
    <Grid container spacing={2} sx={{ py: 1 }}>
      <Grid size={{ xs: 12, md: 3 }}><Facts row={row} /></Grid>
      <Grid size={{ xs: 12, md: 3 }}><SignIn row={row} /></Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <Typography variant="overline" color="text.secondary">{copy.DETAIL_BY_GROUP}</Typography>
        {row.groups.map((group) => (
          <DetailLine key={group.group} label={copy.groupLabel(group.group)} value={group.requests.toLocaleString("en-US")} />
        ))}
        {row.ungrouped_requests > 0 && (
          <DetailLine label={copy.groupLabel("notGrouped")} value={row.ungrouped_requests.toLocaleString("en-US")} />
        )}
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Typography variant="overline" color="text.secondary">{`${copy.DETAIL_RULES} · ${copy.ruleCountLabel(row.rule_count)}`}</Typography>
        {row.top_rules.map((rule) => (
          <DetailLine key={rule.rule_id} label={copy.ruleName(rule.rule_id, rule.label)} value={copy.requestsLabel(rule.requests)}
            caption={rule.cves.join(", ") || undefined} />
        ))}
      </Grid>
    </Grid>
  );
}
